import { Card, CodeViewer, Flex, SimpleTable, Typography } from "djuno-design";

const { Title, Text } = Typography;

const UseShowPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>useShow</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The useShow hook provides a simple and efficient way to manage a
            boolean state, typically for controlling the visibility of
            components or UI elements. It offers three essential functions—show,
            hide, and toggle—for updating the state, along with an optional
            callback function that can be triggered when the show method is
            called.
          </Text>

          <Text size="sm" style={{ marginBottom: 30 }}>
            This hook is useful for managing modal dialogs, dropdowns, or any
            components that need to switch between visible and hidden states.
          </Text>

          <Title level={5}>Usage</Title>
          <Text size="sm" className="mb-2">
            You can use useShow in any functional component to handle visibility
            control. The hook returns an array containing the current visibility
            state and an object with three functions (show, hide, toggle) to
            manage the state.
          </Text>

          <CodeViewer
            language="javascript"
            copyable
            showLineNumbers
            wrapLongLines
            code={`import React from 'react'
import { useShow } from 'djuno-design'

const Modal = () => {
  const [isModalVisible, { show, hide, toggle }] = useShow(false)

  return (
    <div>
      <button onClick={show}>Show Modal</button>
      <button onClick={hide}>Hide Modal</button>
      <button onClick={toggle}>Toggle Modal</button>

      {isModalVisible && (
        <div className="modal">
          <p>Modal Content</p>
          <button onClick={hide}>Close</button>
        </div>
      )}
    </div>
  )
}

export default Modal
`}
          />
        </Flex>

        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Component API
          </Title>

          <Flex direction="col" className="gap-1 w-full">
            <Text size="sm">useShow(initialValue, callback)</Text>
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Prop Name</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* initialValue */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">initialValue</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The initial visibility state (true for visible, false
                        for hidden).
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* callback */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">callback</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link target="_blank" href="" size="xs" code>
                        {`(d?: any) => void (optional)`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A callback function that is triggered whenever the show
                        method is called, with an optional parameter.
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>

        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Return Value
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Return Value</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* isShow */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">isShow</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A boolean value representing the current visibility
                        state (true for visible, false for hidden).
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* show */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">show</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`() => void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to set the state to true (show the
                        component). Accepts an optional parameter for the
                        callback.
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* hide */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">hide</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`() => void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to set the state to false (hide the
                        component).
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* toggle */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">toggle</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`() => void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to toggle the visibility state (true to
                        false, false to true).
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

export default UseShowPage;
